import * as THREE from 'three';
import {
  applySurfaceMaterialDefaults,
  copySurfaceMaterialProperties,
  createSurfaceMaterial,
  surfaceProgramCacheKey,
  type SurfaceMaterialParams,
} from '../core/SurfaceMaterial';
import { applyBakeShaderHooks } from '../core/SurfaceBakeChunks';
import {
  assignDamageFieldUniforms,
  DAMAGE_FIELD_COMMON_GLSL,
  DAMAGE_FIELD_GLSL,
  DAMAGE_FIELD_VERTEX_GLSL,
  DAMAGE_FIELD_VERTEX_SAMPLE,
  damageFieldProgramParts,
  registerDamageFieldConsumer,
} from '../core/DamageFieldBinding';
import {
  applyDamageDecalAtlasDefine,
  assignDamageDecalUniforms,
  DAMAGE_DECAL_ATLAS_DEFINE,
  DAMAGE_DECAL_ATLAS_GLSL,
  DAMAGE_DECAL_VERTEX_GLSL,
  DAMAGE_DECAL_VERTEX_SAMPLE,
} from '../core/DamageDecalAtlas';
import { SURFACE_MICRO_GLSL } from '../core/SurfaceMicroDetail';
import { SURFACE_NOISE_GLSL } from '../core/SurfaceNoise';
import { SURFACE_TRIPLANAR_GLSL } from '../core/TriplanarProjection';
import type { MaterialAppearanceDescriptor } from '../appearanceDescriptor';
import {
  asFiniteNumber,
  asHexColor,
  commonSurfaceControls,
  type SurfaceControlSpec,
  type SurfaceDefinition,
  type SurfaceFace,
} from '../core/SurfaceDefinition';
import { DESTRUCTION_PROFILES } from '../../services/destructionProfiles';

export type StoneQuality = 'high' | 'medium' | 'low';
export type StoneFace = 'exterior' | 'interior';
export type StoneSurfaceState = 'hero' | 'weathered' | 'wet' | 'aggregate';

export interface StoneMaterialSettings {
  /** Physical macro scale in metres per noise tile. */
  textureScale: number;
  roughness: number;
  normalStrength: number;
  macroVariation: number;
  quality: StoneQuality;
  seed: number;
  surfaceState: StoneSurfaceState;
  baseTint: string;
  roughnessMin: number;
  roughnessMax: number;
  wearAmount: number;
  /** Width/height jitter + joint waviness. Default 0.55 — the product look. */
  blockIrregularity: number;
}

export const DEFAULT_STONE_SETTINGS: StoneMaterialSettings = {
  textureScale: 0.9,
  roughness: 0.88,
  normalStrength: 0.16,
  macroVariation: 0.34,
  quality: 'medium',
  seed: 5704,
  surfaceState: 'hero',
  baseTint: '#ffffff',
  roughnessMin: 0.38,
  roughnessMax: 0.96,
  wearAmount: 0,
  blockIrregularity: 0.55,
};

/** Cool grey heavy fines — fewer, larger, faster-falling. */
export const appearanceDescriptor = {
  version: 1,
  dust: { color: '#7d858e', lingerScale: 0.7, settleSpeed: 0.38 },
  chips: { color: '#6e747a' },
  debrisTint: '#5a6168',
  particleStyle: { heavyFines: true },
  fragment: { outer: '#6a6e74', interior: '#9aa0a8' },
} as const satisfies MaterialAppearanceDescriptor;

interface StoneUniforms {
  uStoneTextureScale: { value: number };
  uStoneRoughness: { value: number };
  uStoneNormalStrength: { value: number };
  uStoneMacroVariation: { value: number };
  uStoneQuality: { value: number };
  uStoneSeed: { value: number };
  uStoneInteriorMode: { value: number };
  uStoneSurfaceState: { value: number };
  uStoneBaseTint: { value: THREE.Color };
  uStoneRoughnessMin: { value: number };
  uStoneRoughnessMax: { value: number };
  uStoneWearAmount: { value: number };
  uStoneBlockIrregularity: { value: number };
}

interface StoneMaterialState {
  face: StoneFace;
  settings: StoneMaterialSettings;
  uniforms: StoneUniforms;
}

const stoneStates = new WeakMap<THREE.Material, StoneMaterialState>();

const STONE_GLSL = /* glsl */ `
uniform float uStoneTextureScale;
uniform float uStoneRoughness;
uniform float uStoneNormalStrength;
uniform float uStoneMacroVariation;
uniform float uStoneQuality;
uniform float uStoneSeed;
uniform float uStoneInteriorMode;
uniform float uStoneSurfaceState;
uniform vec3 uStoneBaseTint;
uniform float uStoneRoughnessMin;
uniform float uStoneRoughnessMax;
uniform float uStoneWearAmount;
uniform float uStoneBlockIrregularity;

float gStoneJoint = 0.0;
float gStoneBlockId = 0.5;
float gStoneBlockId2 = 0.5;
float gStoneEdgeWear = 0.0;
float gStoneJointAO = 0.0;
float gStoneBlockKeep = 1.0;
float gStoneFacetId = 0.5;
float gStoneFacetEdge = 1.0;
float gStoneRow = 0.0;
float gStoneCol = 0.0;
float gStoneEdgeX = 0.5;
float gStoneEdgeY = 0.5;
float gStoneFaceX = 0.5;
float gStoneFaceY = 0.5;
float gStoneBlockW = 1.05;
float gStoneCourseH = 0.58;

void stoneFacetCell(vec3 p, float seedPhase, out float id, out float edge) {
  vec3 cell = floor(p);
  float best = 10.0;
  float second = 10.0;
  vec3 bestC = cell;
  for (int k = -1; k <= 1; k++) {
    for (int j = -1; j <= 1; j++) {
      for (int i = -1; i <= 1; i++) {
        vec3 c = cell + vec3(float(i), float(j), float(k));
        vec3 o = vec3(
          surfaceHash_(c, seedPhase),
          surfaceHash_(c + vec3(2.1, 0.3, 0.7), seedPhase),
          surfaceHash_(c + vec3(0.5, 1.9, 1.3), seedPhase)
        );
        float d = length(p - c - o);
        if (d < best) {
          second = best;
          best = d;
          bestC = c;
        } else if (d < second) {
          second = d;
        }
      }
    }
  }
  id = surfaceHash_(bestC + vec3(4.0, 9.0, 2.0), seedPhase);
  edge = clamp((second - best) * 3.2, 0.0, 1.0);
}

float stoneFbm(vec3 p, float wavelengthMeters, float fp) {
  float value = surfaceNoiseBL(p, uStoneSeed * 13.17, wavelengthMeters, fp);
  float weight = 1.0;
  if (uStoneQuality > 0.5) {
    p = p * 2.03 + vec3(17.0, 43.0, 29.0);
    value += surfaceNoiseBL(p, uStoneSeed * 13.17, wavelengthMeters / 2.03, fp) * 0.5;
    weight += 0.5;
  }
  if (uStoneQuality > 1.5) {
    p = p * 2.01 + vec3(31.0, 11.0, 47.0);
    value += surfaceNoiseBL(p, uStoneSeed * 13.17, wavelengthMeters / 4.0803, fp) * 0.25;
    weight += 0.25;
  }
  return value / weight;
}

void stoneSurfaceFields(
  vec3 restPos,
  vec3 restNormal,
  out float broad,
  out float mineral,
  out float pits,
  out float height,
  out float veins
) {
  float scale = max(uStoneTextureScale, 0.05);
  float fp = surfacePixelFootprint(restPos);
  vec3 p = restPos / scale;
  vec3 n = normalize(restNormal);
  vec3 w = surfaceTriplanarWeights(n);
  vec2 front = restPos.xy;
  vec2 side = restPos.zy;
  vec2 top = restPos.xz;
  vec2 uv = (front * w.z + side * w.x + top * w.y) / scale;

  // Coursed irregular ashlar. Course heights hash to mix(0.62, 1.42) of meanH
  // via 1D Worley spacing (no running-sum loop — ANGLE dropped those).
  // Block widths are hash-driven cuts (occasional double / half), not a fixed
  // period. blockIrregularity (default 0.55) adds extra jitter + joint waviness.
  float meanH = 0.58;
  float meanW = 1.05;
  float irr = clamp(uStoneBlockIrregularity, 0.0, 1.0);
  float waveWl = scale * 0.22;
  float waveFade = surfaceOctaveFade(waveWl, fp);
  float waveX = surfaceNoiseBL(vec3(uv.x * 1.7, uv.y * 0.55, 0.0) + vec3(5.0, 1.0, 9.0), uStoneSeed * 13.17, waveWl, fp);
  float waveY = surfaceNoiseBL(vec3(uv.x * 0.6, uv.y * 1.9, 0.0) + vec3(2.0, 11.0, 4.0), uStoneSeed * 13.17, waveWl, fp);
  uv.x += (waveX - 0.5) * 0.045 * irr * waveFade;
  uv.y += (waveY - 0.5) * 0.028 * irr * waveFade;

  float yCell = uv.y / meanH;
  float yBase = floor(yCell);
  float yBest = 100.0;
  float ySecond = 100.0;
  float row = yBase;
  float yFeatN = yBase;
  float yFeatS = yBase + 1.0;
  for (int j = 0; j < 7; j++) {
    float c = yBase + float(j) - 3.0;
    float hh = surfaceHash_(vec3(c, 3.0, 11.0), uStoneSeed * 13.17);
    float skip = surfaceHash_(vec3(c, 4.0, 13.0), uStoneSeed * 13.17);
    if (skip <= 0.86) {
      float jit = mix(0.18, 0.82, hh);
      jit += (surfaceHash_(vec3(c, 8.0, 2.0), uStoneSeed * 13.17) - 0.5) * 0.14 * irr;
      float feat = c + jit;
      float d = abs(yCell - feat);
      if (d < yBest) {
        ySecond = yBest;
        yFeatS = yFeatN;
        yBest = d;
        yFeatN = feat;
        row = c;
      } else if (d < ySecond) {
        ySecond = d;
        yFeatS = feat;
      }
    }
  }
  float courseH = max(yBest + ySecond, 0.30) * meanH;
  float edgeY = clamp((ySecond - yBest) * 0.5 / max(yBest + ySecond, 1e-4), 0.0, 0.5);

  float stagger = mod(abs(row), 2.0) * 0.22;
  float xCell = uv.x / meanW - stagger;
  float xBase = floor(xCell);
  float xBest = 100.0;
  float xSecond = 100.0;
  float col = xBase;
  float xFeatN = xBase;
  float xFeatS = xBase + 1.0;
  for (int i = 0; i < 9; i++) {
    float c = xBase + float(i) - 4.0;
    float hh = surfaceHash_(vec3(c, row, 5.0), uStoneSeed * 13.17);
    float h2 = surfaceHash_(vec3(c, row, 19.0), uStoneSeed * 13.17);
    if (h2 <= 0.84) {
      float jit = mix(0.16, 0.84, hh);
      jit += (h2 - 0.5) * 0.16 * irr;
      float feat = c + jit;
      float d = abs(xCell - feat);
      if (d < xBest) {
        xSecond = xBest;
        xFeatS = xFeatN;
        xBest = d;
        xFeatN = feat;
        col = c;
      } else if (d < xSecond) {
        xSecond = d;
        xFeatS = feat;
      }
    }
    if (h2 < 0.11) {
      float feat2 = c + 0.47 + (hh - 0.5) * 0.10;
      float d2 = abs(xCell - feat2);
      if (d2 < xBest) {
        xSecond = xBest;
        xFeatS = xFeatN;
        xBest = d2;
        xFeatN = feat2;
        col = c + 0.37;
      } else if (d2 < xSecond) {
        xSecond = d2;
        xFeatS = feat2;
      }
    }
  }
  float blockW = max(xBest + xSecond, 0.22) * meanW;
  float edgeX = clamp((xSecond - xBest) * 0.5 / max(xBest + xSecond, 1e-4), 0.0, 0.5);
  gStoneBlockId = surfaceHash_(vec3(row, col, 7.0), uStoneSeed * 13.17);
  gStoneBlockId2 = surfaceHash_(vec3(row, col, 23.0), uStoneSeed * 13.17);
  gStoneRow = row;
  gStoneCol = col;
  gStoneEdgeX = edgeX;
  gStoneEdgeY = edgeY;
  gStoneBlockW = max(blockW, 0.22);
  gStoneCourseH = max(courseH, 0.22);
  gStoneFaceX = clamp((xCell - min(xFeatN, xFeatS)) / max(abs(xFeatN - xFeatS), 1e-4), 0.0, 1.0);
  gStoneFaceY = clamp((yCell - min(yFeatN, yFeatS)) / max(abs(yFeatN - yFeatS), 1e-4), 0.0, 1.0);

  // Joint width in metres, mapped back to a shared edge-unit so the
  // symmetric smoothstep (jointW ± jointHalfW) stays in the same form as brick.
  float metersPerEdge = meanH * scale;
  float dX = edgeX * blockW * scale / max(metersPerEdge, 1e-6);
  float dY = edgeY * courseH * scale / max(metersPerEdge, 1e-6);
  float edgeMin = min(dX, dY);
  float jointW = 0.050;
  // ALL widths and contrast fades derive from the SMOOTH rest-space footprint
  // (fp = fwidth of a smooth varying). NEVER from fwidth of dX/dY/edgeMin: the
  // Worley distance field is discontinuous across cell seams and the min() axis
  // flip, so its per-pixel derivative flickers — a visibility fade built on it
  // punched single-pixel holes in the mortar that rasterized as dashed lines
  // (high-contrast against dark blocks; survived four term-level fix attempts).
  float fpEdge = fp / max(metersPerEdge, 1e-6);
  float nearHoriz = step(dY, dX);
  float jointHalfW = max(0.055, fpEdge * 1.4);
  float joint = 1.0 - smoothstep(jointW - jointHalfW, jointW + jointHalfW, edgeMin);
  float grooveWidthMeters = 3.0 * jointW * metersPerEdge;
  float grooveFade = surfaceOctaveFade(grooveWidthMeters, fp);
  // Sub-pixel joints fade CONTRAST (energy conservation), never dash. Fade only
  // below ~1px so mortar stays legible at working wide framing; the dash bug was
  // the fade's INSTABILITY (fwidth-of-field), not its strength.
  float jointVis = clamp(jointW / max(fpEdge * 0.8, 1e-6), 0.0, 1.0);
  gStoneJoint = joint * grooveFade * jointVis;
  // Quintic (C2) so per-block height/tint does not cliff at the Voronoi edge —
  // that step was rasterizing as the residual dashed hairline on top of the mortar.
  float blockLin = clamp(1.0 - joint, 0.0, 1.0);
  gStoneBlockKeep = blockLin * blockLin * blockLin * (blockLin * (blockLin * 6.0 - 15.0) + 10.0);

  float aoHalf = max(jointHalfW * 1.8, fpEdge * 1.6);
  float jointAO = 1.0 - smoothstep(jointW - aoHalf, jointW + aoHalf, edgeMin);
  float aoFade = surfaceOctaveFade(grooveWidthMeters * 1.4, fp);
  gStoneJointAO = jointAO * aoFade * jointVis;

  // Per-EDGE wear (block id + nearest-edge axis), not a uniform CAD bevel.
  // Tertiles: heavy chip / light wear / nearly clean. Coarse along-edge
  // meander can drop out entirely. Specular rim uses the same hash.
  float edgeHash = surfaceHash_(vec3(row, col, 31.0 + nearHoriz * 17.0), uStoneSeed * 13.17);
  float edgeHash2 = surfaceHash_(vec3(row, col, 47.0 + nearHoriz * 13.0), uStoneSeed * 13.17);
  float heavyWear = 1.0 - step(0.333, edgeHash);
  float lightWear = step(0.333, edgeHash) * (1.0 - step(0.666, edgeHash));
  float cleanWear = step(0.666, edgeHash);
  float wearAmt = heavyWear * mix(0.72, 1.0, edgeHash2)
                + lightWear * mix(0.12, 0.32, edgeHash2)
                + cleanWear * 0.0;
  float wearNoise = surfaceNoiseBL(p * 1.45 + vec3(11.0, 3.0, 7.0), uStoneSeed * 13.17, scale / 1.45, fp);
  float dropout = smoothstep(0.26, 0.48, wearNoise);
  float wearW = mix(0.050, 0.20, wearNoise) * mix(0.82, 1.18, irr);
  float wearHalf = max(0.040, fpEdge * 1.2);
  float wearMask = 1.0 - smoothstep(wearW - wearHalf, wearW + wearHalf, edgeMin);
  float wearFade = surfaceOctaveFade(max(wearW, 0.06) * metersPerEdge * 2.4, fp);
  // Wear is albedo/roughness ONLY — never height. A height band's inner boundary
  // renders as a derivative shading contour (an inset rectangle) that dashes when
  // the ramp goes sub-pixel; three fix attempts on the contour itself failed.
  // Like jointVis, but stricter: wear is strictly CLOSE-RANGE detail — fade in
  // only once the band spans ~3→6 px of the SMOOTH footprint. At wide framing a
  // half-faded band still reads as a broken bright streak hugging the joint.
  float wearVis = clamp(wearW / max(fpEdge * 3.0, 1e-6) - 1.0, 0.0, 1.0);
  float wearLin = clamp(wearMask * wearFade * wearAmt * dropout * wearVis, 0.0, 1.0);
  float wearH = wearLin * wearLin * wearLin * (wearLin * (wearLin * 6.0 - 15.0) + 10.0);
  gStoneEdgeWear = wearH;

  broad = stoneFbm(p * 0.72 + vec3(5.0, 13.0, 7.0), scale / 0.72, fp);
  mineral = smoothstep(0.62, 0.9, stoneFbm(p * 3.2 + vec3(19.0, 3.0, 31.0), scale / 3.2, fp));
  float pitFade = surfaceOctaveFade(scale / 17.0, fp);
  pits = uStoneQuality > 0.5
    ? smoothstep(0.9 - 0.06 * (1.0 - pitFade), 0.985, surfaceNoiseBL(p * 17.0 + vec3(41.0, 7.0, 23.0), uStoneSeed * 13.17, scale / 17.0, fp)) * pitFade
    : 0.0;
  veins = smoothstep(
    0.48,
    0.72,
    stoneFbm(vec3(p.x * 0.42, p.y * 1.35, p.z * 0.42) + vec3(29.0, 2.0, 11.0), scale / 1.35, fp)
  );
  float irrFade = surfaceOctaveFade(scale / 1.1, fp);
  float irrN = mix(0.5, surfaceNoiseBL(p * 1.1 + vec3(9.0, 2.0, 15.0), uStoneSeed * 13.17, scale / 1.1, fp), irrFade);
  height = 0.5 + (broad - 0.5) * 0.08 + mineral * 0.045 - pits * 0.12 + veins * 0.018
    + (irrN - 0.5) * irr * 0.08
    + (gStoneBlockId - 0.5) * 0.055 * gStoneBlockKeep;
  if (uStoneInteriorMode > 0.5) {
    // Fresh fracture: no joints, no block grid. Angular Worley facets.
    gStoneJoint = 0.0;
    gStoneJointAO = 0.0;
    gStoneEdgeWear = 0.0;
    gStoneBlockKeep = 1.0;
    stoneFacetCell(p * 4.6 + vec3(4.0, 21.0, 8.0), uStoneSeed * 13.17, gStoneFacetId, gStoneFacetEdge);
    float facetQ = floor(gStoneFacetId * 5.0 + 1e-4) / 4.0;
    height = mix(0.34, 0.74, facetQ);
    height = mix(height - 0.10, height, gStoneFacetEdge);
  }
}

float stoneQualityValue() {
  return uStoneQuality;
}
`;

const STONE_VERTEX_INJECTION = /* glsl */ `
attribute vec3 surfaceRestPosition;
varying vec3 vStoneRestPosition;
varying vec3 vStoneRestNormal;
${DAMAGE_FIELD_COMMON_GLSL}
${DAMAGE_FIELD_VERTEX_GLSL}
${DAMAGE_DECAL_VERTEX_GLSL}
`;

const STONE_FRAGMENT_INJECTION = /* glsl */ `
uniform mat3 normalMatrix;
varying vec3 vStoneRestPosition;
varying vec3 vStoneRestNormal;
${SURFACE_TRIPLANAR_GLSL}
${SURFACE_NOISE_GLSL}
${SURFACE_MICRO_GLSL}
${DAMAGE_FIELD_GLSL}
${DAMAGE_DECAL_ATLAS_GLSL}
${STONE_GLSL}
float gStoneBroad;
float gStoneMineral;
float gStonePits;
float gStoneHeight;
float gStoneVeins;
float gStoneMicroField;
`;

function clamp01(value: number, fallback = 0): number {
  return Math.min(Math.max(Number.isFinite(value) ? value : fallback, 0), 1);
}

function qualityValue(quality: StoneQuality): number {
  return quality === 'high' ? 2 : quality === 'medium' ? 1 : 0;
}

function surfaceStateValue(state: StoneSurfaceState): number {
  return state === 'weathered' ? 1 : state === 'wet' ? 2 : state === 'aggregate' ? 3 : 0;
}

function normalizeSettings(settings: StoneMaterialSettings): StoneMaterialSettings {
  return {
    textureScale: Number.isFinite(settings.textureScale) && settings.textureScale > 0
      ? settings.textureScale
      : DEFAULT_STONE_SETTINGS.textureScale,
    roughness: clamp01(settings.roughness, DEFAULT_STONE_SETTINGS.roughness),
    normalStrength: Math.min(Math.max(Number.isFinite(settings.normalStrength) ? settings.normalStrength : 0, 0), 0.8),
    macroVariation: clamp01(settings.macroVariation, DEFAULT_STONE_SETTINGS.macroVariation),
    quality: settings.quality === 'high' || settings.quality === 'low' ? settings.quality : 'medium',
    seed: Number.isFinite(settings.seed) ? settings.seed : DEFAULT_STONE_SETTINGS.seed,
    surfaceState: settings.surfaceState === 'weathered' || settings.surfaceState === 'wet' || settings.surfaceState === 'aggregate'
      ? settings.surfaceState
      : 'hero',
    baseTint: asHexColor(settings.baseTint, DEFAULT_STONE_SETTINGS.baseTint),
    roughnessMin: clamp01(asFiniteNumber(settings.roughnessMin, DEFAULT_STONE_SETTINGS.roughnessMin), DEFAULT_STONE_SETTINGS.roughnessMin),
    roughnessMax: clamp01(asFiniteNumber(settings.roughnessMax, DEFAULT_STONE_SETTINGS.roughnessMax), DEFAULT_STONE_SETTINGS.roughnessMax),
    wearAmount: clamp01(asFiniteNumber(settings.wearAmount, DEFAULT_STONE_SETTINGS.wearAmount), DEFAULT_STONE_SETTINGS.wearAmount),
    blockIrregularity: clamp01(asFiniteNumber(settings.blockIrregularity, DEFAULT_STONE_SETTINGS.blockIrregularity), DEFAULT_STONE_SETTINGS.blockIrregularity),
  };
}

function createUniforms(settings: StoneMaterialSettings, face: StoneFace): StoneUniforms {
  return {
    uStoneTextureScale: { value: settings.textureScale },
    uStoneRoughness: { value: settings.roughness },
    uStoneNormalStrength: { value: settings.normalStrength },
    uStoneMacroVariation: { value: settings.macroVariation },
    uStoneQuality: { value: qualityValue(settings.quality) },
    uStoneSeed: { value: settings.seed },
    uStoneInteriorMode: { value: face === 'interior' ? 1 : 0 },
    uStoneSurfaceState: { value: surfaceStateValue(settings.surfaceState) },
    uStoneBaseTint: { value: new THREE.Color(settings.baseTint) },
    uStoneRoughnessMin: { value: Math.min(settings.roughnessMin, settings.roughnessMax) },
    uStoneRoughnessMax: { value: Math.max(settings.roughnessMin, settings.roughnessMax) },
    uStoneWearAmount: { value: settings.wearAmount },
    uStoneBlockIrregularity: { value: settings.blockIrregularity },
  };
}

function syncUniforms(state: StoneMaterialState): void {
  const { settings, uniforms, face } = state;
  uniforms.uStoneTextureScale.value = settings.textureScale;
  uniforms.uStoneRoughness.value = settings.roughness;
  uniforms.uStoneNormalStrength.value = settings.normalStrength;
  uniforms.uStoneMacroVariation.value = settings.macroVariation;
  uniforms.uStoneQuality.value = qualityValue(settings.quality);
  uniforms.uStoneSeed.value = settings.seed;
  uniforms.uStoneInteriorMode.value = face === 'interior' ? 1 : 0;
  uniforms.uStoneSurfaceState.value = surfaceStateValue(settings.surfaceState);
  uniforms.uStoneBaseTint.value.set(settings.baseTint);
  uniforms.uStoneRoughnessMin.value = Math.min(settings.roughnessMin, settings.roughnessMax);
  uniforms.uStoneRoughnessMax.value = Math.max(settings.roughnessMin, settings.roughnessMax);
  uniforms.uStoneWearAmount.value = settings.wearAmount;
  uniforms.uStoneBlockIrregularity.value = settings.blockIrregularity;
}

function compileStoneShader(
  shader: { uniforms: Record<string, { value: unknown }>; vertexShader: string; fragmentShader: string },
  state: StoneMaterialState,
): void {
  Object.assign(shader.uniforms, state.uniforms);
  assignDamageFieldUniforms(shader);
  assignDamageDecalUniforms(shader);
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', `${STONE_VERTEX_INJECTION}\n#include <common>`)
    .replace(
      '#include <worldpos_vertex>',
      `#include <worldpos_vertex>
vStoneRestPosition = surfaceRestPosition;
vStoneRestNormal = normalize(normal);
${DAMAGE_FIELD_VERTEX_SAMPLE}
${DAMAGE_DECAL_VERTEX_SAMPLE}
`,
    );

  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', `${STONE_FRAGMENT_INJECTION}\n#include <common>`)
    .replace(
      '#include <color_fragment>',
      `#include <color_fragment>
gSurfaceSpecAaRough = 0.0;
stoneSurfaceFields(
  vStoneRestPosition,
  vStoneRestNormal,
  gStoneBroad,
  gStoneMineral,
  gStonePits,
  gStoneHeight,
  gStoneVeins
);
gStoneMicroField = surfaceMicroField(
  vStoneRestPosition,
  max(uStoneTextureScale * 0.1, 0.02),
  uStoneSeed + 41.0
);
float stoneLodFade = surfaceMicroFade(vStoneRestPosition, max(uStoneTextureScale * 0.1, 0.02));
float stoneMacro = (gStoneBroad - 0.5) * 2.0 * uStoneMacroVariation;
vec3 stoneBase = mix(vec3(0.12, 0.14, 0.15), vec3(0.28, 0.3, 0.31), gStoneBroad);
vec3 stoneMineralTint = mix(vec3(0.21, 0.23, 0.24), vec3(0.38, 0.36, 0.31), gStoneMineral);
vec3 stoneAlbedo = mix(stoneBase, stoneMineralTint, gStoneMineral * 0.26);
stoneAlbedo = mix(stoneAlbedo, vec3(0.08, 0.09, 0.1), gStonePits * 0.72);
stoneAlbedo = mix(stoneAlbedo, vec3(0.38, 0.32, 0.25), gStoneVeins * 0.16);
float stoneGrain = stoneFbm(
  vStoneRestPosition / max(uStoneTextureScale, 0.05) * 18.0 + vec3(7.0, 23.0, 11.0),
  max(uStoneTextureScale, 0.05) / 18.0,
  surfacePixelFootprint(vStoneRestPosition)
);
stoneAlbedo *= (0.86 + stoneGrain * 0.36) * (1.0 + stoneMacro * 0.22);
float stonePoreWl = max(uStoneTextureScale, 0.05) / 28.0;
float stonePoreFp = surfacePixelFootprint(vStoneRestPosition);
float stonePore = surfaceNoiseBL(
  vStoneRestPosition / max(uStoneTextureScale, 0.05) * 28.0 + vec3(3.0, 9.0, 15.0),
  uStoneSeed * 13.17,
  stonePoreWl,
  stonePoreFp
);
stoneAlbedo *= mix(0.90, 1.08, stonePore);
stoneAlbedo *= 0.94 + gStoneMicroField * 0.14;
// Per-block tint/value (hash by block id) so courses read as individual stones.
// Stronger dusk-safe spread: some blocks distinctly darker / warmer (weathered granite).
stoneAlbedo *= mix(1.0, mix(0.70, 1.16, gStoneBlockId), gStoneBlockKeep);
stoneAlbedo = mix(stoneAlbedo, stoneAlbedo * vec3(1.10, 0.94, 0.78), gStoneBlockId2 * 0.42 * gStoneBlockKeep);
stoneAlbedo = mix(stoneAlbedo, stoneAlbedo * vec3(0.84, 0.88, 0.94), (1.0 - gStoneBlockId2) * 0.22 * gStoneBlockKeep);
vec3 stoneJointTint = vec3(0.07, 0.075, 0.08);
stoneAlbedo = mix(stoneAlbedo, stoneJointTint, gStoneJoint * 0.72);
// Irregular edge wear in albedo (dust / chipped arris) — replaces the uniform machine bevel.
stoneAlbedo = mix(
  stoneAlbedo,
  stoneAlbedo * mix(vec3(0.78, 0.76, 0.72), vec3(1.08, 1.04, 0.96), gStoneBlockId2),
  gStoneEdgeWear * 0.38
);
if (uStoneInteriorMode > 0.5) {
  // Fresh-break face. Tonal anchor: appearance.fragment.interior #9aa0a8.
  // Angular facet patches with distinct value steps — not smooth marble mottle.
  vec3 stoneInteriorTone = vec3(0.604, 0.627, 0.659);
  float facetQ = floor(gStoneFacetId * 5.0 + 1e-4) / 4.0;
  stoneAlbedo = mix(stoneInteriorTone * 0.38, stoneInteriorTone * 1.36, facetQ);
  stoneAlbedo = mix(stoneAlbedo * vec3(0.30, 0.32, 0.36), stoneAlbedo, smoothstep(0.0, 0.22, gStoneFacetEdge));
  stoneAlbedo *= 0.96 + gStoneMicroField * 0.08;
}
if (uStoneSurfaceState > 0.5 && uStoneSurfaceState < 1.5) {
  stoneAlbedo *= mix(1.0, 0.82, gStoneBroad * 0.45);
} else if (uStoneSurfaceState > 1.5 && uStoneSurfaceState < 2.5) {
  stoneAlbedo *= mix(1.0, 0.72, gStoneMineral * 0.45);
} else if (uStoneSurfaceState > 2.5) {
  stoneAlbedo = mix(stoneAlbedo, stoneMineralTint * 1.12, gStoneMineral * 0.35);
}
#ifdef USE_DAMAGE_FIELD
fillDamageGlobals(vStoneRestPosition, normalize(vStoneRestNormal));
float stoneFresh = 1.0 - gDamageAge * 0.32;
vec3 stoneDamageInterior = vec3(0.604, 0.627, 0.659);
#ifdef USE_DAMAGE_DECAL_ATLAS
sampleStoneDamageDecals(
  vStoneRestPosition,
  normalize(vStoneRestNormal),
  uStoneTextureScale,
  gStoneJoint,
  gStoneEdgeX,
  gStoneEdgeY,
  gStoneRow,
  gStoneCol,
  gStoneFaceX,
  gStoneFaceY,
  gStoneBlockW,
  gStoneCourseH
);
stoneAlbedo = mix(stoneAlbedo, stoneAlbedo * vec3(0.90, 0.90, 0.88), gDamageBlotch * 0.04 * stoneFresh);
float stoneDmgFp = surfacePixelFootprint(vStoneRestPosition);
float stoneDmgM = max(uStoneTextureScale, 0.05);
vec3 stoneDmgP = vStoneRestPosition * (1.0 / stoneDmgM);
// Crater grain needs a COARSE octave: the 18x/36x octaves (2.5-5cm) fall below
// the pixel footprint at gameplay distance, band-limit to their 0.5 mean, and
// leave chips flat white. The 6x octave (~15cm) survives at every framing.
float gStoneCraterGrain = surfaceNoiseBL(stoneDmgP * 6.0 + vec3(7.0, 13.0, 2.0), uStoneSeed * 13.17, stoneDmgM / 6.0, stoneDmgFp) * 0.45
  + surfaceNoiseBL(stoneDmgP * 18.0 + vec3(5.0, 2.0, 9.0), uStoneSeed * 13.17, stoneDmgM / 18.0, stoneDmgFp) * 0.30
  + surfaceNoiseBL(stoneDmgP * 36.0 + vec3(11.0, 4.0, 3.0), uStoneSeed * 13.17, stoneDmgM / 36.0, stoneDmgFp) * 0.25;
float gStoneChipHash = surfaceHash_(floor(stoneDmgP * 28.0), uStoneSeed * 13.17);
gStoneCraterGrain = mix(gStoneCraterGrain, mix(0.22, 0.82, gStoneChipHash), 0.38);
vec3 stoneSpallMean = mix(stoneDamageInterior * 1.02, stoneDamageInterior * 0.62, clamp(gDecalDepth * 1.6, 0.0, 1.0));
stoneSpallMean *= mix(0.74, 1.26, gStoneCraterGrain);
stoneSpallMean = mix(stoneSpallMean, stoneSpallMean * vec3(0.86, 0.86, 0.84), gDecalCrack * 0.22);
float stoneSpallAmt = gDecalSpall * mix(0.88, 1.0, clamp(gDecalDepth, 0.0, 1.0)) * (1.0 - gStoneSkid * 0.22);
stoneAlbedo = mix(stoneAlbedo, stoneSpallMean, stoneSpallAmt);
vec3 stoneSkidTint = mix(stoneDamageInterior * 1.06, stoneAlbedo * vec3(1.18, 1.14, 1.08), 0.25);
stoneAlbedo = mix(stoneAlbedo, stoneSkidTint, gStoneSkid * 0.90);
vec3 stoneRimTint = mix(stoneDamageInterior * 1.24, stoneAlbedo * vec3(1.20, 1.16, 1.10), 0.3);
stoneAlbedo = mix(stoneAlbedo, stoneRimTint, gDecalRim * 0.95 * gDecalFresh);
vec3 stoneJointDark = vec3(0.070, 0.075, 0.080);
stoneAlbedo *= mix(vec3(1.0), vec3(0.58, 0.58, 0.56), clamp(gStoneJointCrack, 0.0, 1.0));
stoneAlbedo = mix(stoneAlbedo, stoneJointDark, clamp(gStoneJointHair, 0.0, 1.0));
#else
stoneAlbedo = mix(stoneAlbedo, stoneDamageInterior, gDamageBlotch * mix(0.28, 0.58, clamp(gDamage, 0.0, 1.0)) * stoneFresh);
stoneAlbedo = mix(stoneAlbedo, stoneAlbedo * vec3(0.14, 0.15, 0.16), gDamageCore * 0.82 * stoneFresh);
stoneAlbedo = mix(stoneAlbedo, stoneAlbedo * vec3(0.14, 0.15, 0.16), gDamageCrack * stoneFresh);
#endif
#endif
stoneAlbedo *= uStoneBaseTint;
stoneAlbedo = mix(stoneAlbedo, stoneAlbedo * vec3(0.82, 0.80, 0.76), clamp(uStoneWearAmount, 0.0, 1.0));
diffuseColor.rgb = stoneAlbedo;`,
    )
    .replace(
      '#include <roughnessmap_fragment>',
      `#include <roughnessmap_fragment>
roughnessFactor = clamp(
  uStoneRoughness + (gStoneBroad - 0.5) * 0.12 + gStonePits * 0.12 - gStoneMineral * 0.04 + gStoneJoint * 0.14
    + gStoneEdgeWear * 0.10,
  0.38,
  0.96
);
if (uStoneInteriorMode > 0.5) {
  roughnessFactor = clamp(roughnessFactor + 0.08, 0.42, 0.98);
}
if (uStoneSurfaceState > 1.5 && uStoneSurfaceState < 2.5) {
  roughnessFactor = clamp(roughnessFactor - gStoneMineral * 0.28, 0.18, 0.96);
}
roughnessFactor = clamp(roughnessFactor + (gStoneMicroField - 0.5) * 0.07, 0.18, 0.98);
roughnessFactor = clamp(roughnessFactor + (1.0 - stoneLodFade) * 0.05, 0.18, 0.98);
// Sink Toksvig slot (filled after surfaceHeightRestNormal in normal_fragment_maps; reapplied there).
roughnessFactor = clamp(roughnessFactor + gSurfaceSpecAaRough, 0.18, 0.98);
#ifdef USE_DAMAGE_FIELD
#ifdef USE_DAMAGE_DECAL_ATLAS
roughnessFactor = clamp(
  roughnessFactor
    + gDamageBlotch * 0.03
    + gDecalSpall * 0.12
    + gDecalDepth * 0.08
    + gStoneJointCrack * 0.10
    + gStoneJointHair * 0.06
    + gStoneSkid * 0.05
    + gStoneCornerChip * 0.04,
  0.18,
  0.98
);
#else
roughnessFactor = clamp(roughnessFactor + gDamageBlotch * 0.07 + gDamageCrack * 0.08, 0.18, 0.98);
#endif
#endif
`
    )
    .replace(
      '#include <normal_fragment_maps>',
      `#include <normal_fragment_maps>
vec3 stoneBaseNormal = normalize(vStoneRestNormal);
vec3 stoneRelief = surfaceHeightRestNormal(
  stoneBaseNormal,
  vStoneRestPosition,
  gStoneHeight,
  uStoneNormalStrength * 0.7 * surfaceOctaveFade(max(uStoneTextureScale, 0.05) / 17.0, surfacePixelFootprint(vStoneRestPosition))
);
normal = normalize(normal + normalMatrix * (stoneRelief - stoneBaseNormal));
vec3 stoneMicroBump = surfaceMicroNormal(
  stoneBaseNormal,
  vStoneRestPosition,
  max(uStoneTextureScale * 0.1, 0.02),
  uStoneNormalStrength * 0.24,
  uStoneSeed + 41.0
);
normal = normalize(normal + normalMatrix * (stoneMicroBump - stoneBaseNormal));
// Apply sink Toksvig after height normals fill gSurfaceSpecAaRough (three.js runs this after roughnessmap_fragment).
roughnessFactor = clamp(roughnessFactor + gSurfaceSpecAaRough, 0.18, 0.98);
float stoneRSpan = max(0.58, 1e-4);
float stoneRT = clamp((roughnessFactor - 0.38) / stoneRSpan, 0.0, 1.0);
roughnessFactor = mix(uStoneRoughnessMin, max(uStoneRoughnessMin, uStoneRoughnessMax), stoneRT);`
    )
    .replace(
      '#include <aomap_fragment>',
      `#include <aomap_fragment>
reflectedLight.indirectDiffuse *= 1.0 - gStonePits * 0.18 - gStoneJointAO * 0.14;
#ifdef USE_DAMAGE_FIELD
#ifdef USE_DAMAGE_DECAL_ATLAS
reflectedLight.indirectDiffuse *= 1.0 - gDecalAo * 0.42 - gDecalDepth * 0.10;
#endif
#endif`,
    );
}

function applyStoneDefaults(material: THREE.MeshStandardMaterial, settings: StoneMaterialSettings): void {
  applySurfaceMaterialDefaults(material, {
    roughness: settings.roughness,
    metalness: 0,
    shaderOwnsAlbedo: true,
  });
}

export function createStoneMaterial(
  params: SurfaceMaterialParams,
  settings: StoneMaterialSettings,
  face: StoneFace,
): THREE.MeshStandardMaterial {
  const normalized = normalizeSettings(settings);
  const material = createSurfaceMaterial(params, {
    roughness: normalized.roughness,
    metalness: 0,
    shaderOwnsAlbedo: true,
  });
  const state: StoneMaterialState = {
    face,
    settings: normalized,
    uniforms: createUniforms(normalized, face),
  };
  applyStoneDefaults(material, normalized);
  material.onBeforeCompile = (shader) => {
    compileStoneShader(shader, state);
    applyBakeShaderHooks(shader, material);
  };
  const decalEnabled = face === 'exterior';
  if (decalEnabled) applyDamageDecalAtlasDefine(material);
  material.customProgramCacheKey = () =>
    surfaceProgramCacheKey(
      'stone',
      9,
      state.face,
      state.settings.quality,
      decalEnabled ? DAMAGE_DECAL_ATLAS_DEFINE : 'no-decal',
      ...damageFieldProgramParts(),
    );
  stoneStates.set(material, state);
  registerDamageFieldConsumer(material);
  return material;
}

export function isStoneMaterial(material: THREE.Material): material is THREE.MeshStandardMaterial {
  return material instanceof THREE.MeshStandardMaterial && stoneStates.has(material);
}

export function updateStoneMaterial(material: THREE.Material, settings: StoneMaterialSettings): void {
  const state = stoneStates.get(material);
  if (!state) throw new TypeError('updateStoneMaterial expects a material created by createStoneMaterial');
  state.settings = normalizeSettings(settings);
  syncUniforms(state);
  if (material instanceof THREE.MeshStandardMaterial) {
    applyStoneDefaults(material, state.settings);
    material.needsUpdate = true;
  }
}

export function cloneStoneMaterial(source: THREE.Material): THREE.MeshStandardMaterial {
  const state = stoneStates.get(source);
  if (!state || !(source instanceof THREE.MeshStandardMaterial)) {
    throw new TypeError('cloneStoneMaterial expects a material created by createStoneMaterial');
  }
  const clone = createStoneMaterial(
    {
      color: source.color.clone(),
      roughness: source.roughness,
      metalness: source.metalness,
      flatShading: source.flatShading,
      side: source.side,
      transparent: source.transparent,
      opacity: source.opacity,
      depthWrite: source.depthWrite,
      alphaTest: source.alphaTest,
      vertexColors: source.vertexColors,
      name: source.name,
    },
    state.settings,
    state.face,
  );
  copySurfaceMaterialProperties(source, clone);
  return clone;
}

const STONE_CONTROLS: SurfaceControlSpec[] = [
  ...commonSurfaceControls(DEFAULT_STONE_SETTINGS),
  {
    key: 'blockIrregularity',
    label: 'Block irregularity',
    kind: 'range',
    min: 0,
    max: 1,
    step: 0.01,
    default: DEFAULT_STONE_SETTINGS.blockIrregularity,
    group: 'detail',
  },
  {
    key: 'macroVariation',
    label: 'Macro variation',
    kind: 'range',
    min: 0,
    max: 1,
    step: 0.01,
    default: DEFAULT_STONE_SETTINGS.macroVariation,
    group: 'detail',
  },
];

export const SURFACE_DEFINITION: SurfaceDefinition<StoneMaterialSettings> = {
  id: 'stone',
  label: 'Stone',
  appearance: appearanceDescriptor,
  physics: DESTRUCTION_PROFILES.stone,
  defaultSettings: DEFAULT_STONE_SETTINGS,
  controls: STONE_CONTROLS,
  settingsFromControls(values) {
    return normalizeSettings({
      ...DEFAULT_STONE_SETTINGS,
      seed: asFiniteNumber(values.seed, DEFAULT_STONE_SETTINGS.seed),
      textureScale: asFiniteNumber(values.textureScale, DEFAULT_STONE_SETTINGS.textureScale),
      baseTint: asHexColor(values.baseTint, DEFAULT_STONE_SETTINGS.baseTint),
      roughnessMin: asFiniteNumber(values.roughnessMin, DEFAULT_STONE_SETTINGS.roughnessMin),
      roughnessMax: asFiniteNumber(values.roughnessMax, DEFAULT_STONE_SETTINGS.roughnessMax),
      normalStrength: asFiniteNumber(values.normalStrength, DEFAULT_STONE_SETTINGS.normalStrength),
      wearAmount: asFiniteNumber(values.wearAmount, DEFAULT_STONE_SETTINGS.wearAmount),
      blockIrregularity: asFiniteNumber(values.blockIrregularity, DEFAULT_STONE_SETTINGS.blockIrregularity),
      macroVariation: asFiniteNumber(values.macroVariation, DEFAULT_STONE_SETTINGS.macroVariation),
    });
  },
  createMaterial(params, settings, face: SurfaceFace) {
    return createStoneMaterial(params, settings, face);
  },
  updateMaterial: updateStoneMaterial,
  cloneMaterial: cloneStoneMaterial,
  isMaterial: isStoneMaterial,
};
